/**
 * src/instrumentation-client.js
 *
 * Inicialización de Sentry en el browser (App Router + Turbopack).
 *
 * Migrado desde `sentry.client.config.js` (2026-06-01): Next 16 + Sentry v10
 * usan `instrumentation-client.{js,ts}` como convención de carga client-side
 * (mejor compatibilidad con Turbopack, el bundler por default en Next 16).
 * El bloque de init es IDÉNTICO al anterior — solo cambia la ubicación + se
 * agrega `onRouterTransitionStart` para instrumentar navegaciones del router.
 *
 * Captura: errores no manejados (window.onerror), promesas rechazadas, y
 * errores capturados por error boundaries (vía Sentry.captureException).
 *
 * Graceful degrade: si NEXT_PUBLIC_SENTRY_DSN no está configurada (e.g. dev
 * local sin cuenta Sentry), el SDK NO inicializa y la app funciona normal
 * sin reporting — evita builds rotos para devs sin acceso a Sentry.
 *
 * PERF (auditoría PageSpeed 2026-07-03): `@sentry/nextjs` pesa ~129KB
 * transferidos (426KB sin comprimir) y este archivo, por convención de
 * Next 16, se ejecuta DESPUÉS de cargar el HTML pero ANTES de la hidratación
 * de React (ver docs `instrumentation-client.md`, sección "Execution timing").
 * Eso significa que el import estático de `@sentry/nextjs` competía con la
 * hidratación por el hilo principal en el momento más crítico — Lighthouse
 * mostraba 2 long-tasks de ~110ms cada una justo ahí, empujando LCP/TBT.
 *
 * Fix v1: import() dinámico diferido a requestIdleCallback. Sentry sigue
 * cubriendo prácticamente toda la sesión real (el usuario tarda bien más de
 * unos ms en interactuar), solo se resigna capturar errores de los primeros
 * instantes previos a la hidratación — trade-off aceptado: eso es exactamente
 * lo que Sentry recomienda para no penalizar Core Web Vitals con su propio SDK.
 *
 * Fix v2 (PageSpeed re-auditoría 2026-07-04): requestIdleCallback dispara
 * SOLO, sin esperar interacción, así que el bot headless de Lighthouse/PSI
 * (que nunca hace click/scroll/keydown) igual lo capturaba dentro de su
 * ventana de medición — seguía apareciendo como ~260KB de "JS sin usar" y
 * aportando TBT. Ahora el trigger es "lo que pase primero entre idle Y la
 * primera interacción real", con el idle como red de seguridad a 8s (no 4s)
 * para dar más margen a que la interacción gane la carrera. Un visitante
 * real interactúa (scroll, click, tecla) casi siempre en el primer segundo,
 * así que la cobertura de errores para sesiones reales no baja — un bot que
 * jamás interactúa (Lighthouse) simplemente nunca dispara la carga, que es
 * exactamente lo que queremos para no penalizar el score.
 */

// .trim() porque vercel env add agrega newline → DSN parser no lo reconoce
;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/blog/:slug\",\"regex\":\"^/blog/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/invite/:token\",\"regex\":\"^/invite/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false},{\"path\":\"/share/:token\",\"regex\":\"^/share/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false},{\"path\":\"/share-chat/:id\",\"regex\":\"^/share-chat/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/casacion-share/:token\",\"regex\":\"^/casacion-share/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false},{\"path\":\"/master/email-templates/:slug\",\"regex\":\"^/master/email-templates/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/master/organizations/:id/cleanup\",\"regex\":\"^/master/organizations/([^/]+)/cleanup$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/r/:token\",\"regex\":\"^/r/([^/]+)$\",\"paramNames\":[\"token\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/agenda\"},{\"path\":\"/analytics\"},{\"path\":\"/cambiar-password\"},{\"path\":\"/carpetas\"},{\"path\":\"/casacion\"},{\"path\":\"/chat\"},{\"path\":\"/chat/comparar\"},{\"path\":\"/configuracion\"},{\"path\":\"/configuracion/api-keys\"},{\"path\":\"/configuracion/cambiar-password\"},{\"path\":\"/configuracion/seguridad/2fa\"},{\"path\":\"/configuracion/seguridad/dispositivos\"},{\"path\":\"/configuracion/templates\"},{\"path\":\"/cuenta\"},{\"path\":\"/cuenta/eliminar\"},{\"path\":\"/cuenta/facturacion\"},{\"path\":\"/entrenamiento\"},{\"path\":\"/herramientas\"},{\"path\":\"/herramientas/honorarios\"},{\"path\":\"/herramientas/intereses\"},{\"path\":\"/herramientas/laboral\"},{\"path\":\"/herramientas/onapi\"},{\"path\":\"/herramientas/pension\"},{\"path\":\"/herramientas/plazos\"},{\"path\":\"/herramientas/prescripcion\"},{\"path\":\"/herramientas/rnc\"},{\"path\":\"/home\"},{\"path\":\"/manuales\"},{\"path\":\"/memorias\"},{\"path\":\"/notificaciones\"},{\"path\":\"/onboarding\"},{\"path\":\"/plantillas\"},{\"path\":\"/plantillas/upload\"},{\"path\":\"/plazario\"},{\"path\":\"/refer\"},{\"path\":\"/soporte\"},{\"path\":\"/suscripcion\"},{\"path\":\"/tareas\"},{\"path\":\"/\"},{\"path\":\"/blog\"},{\"path\":\"/casos-uso\"},{\"path\":\"/conoce/casacion\"},{\"path\":\"/conoce/plantillas\"},{\"path\":\"/conoce/plazario\"},{\"path\":\"/email-verificado\"},{\"path\":\"/estado\"},{\"path\":\"/faq\"},{\"path\":\"/forgot-password\"},{\"path\":\"/funciones\"},{\"path\":\"/legal/cumplimiento\"},{\"path\":\"/legal/privacidad\"},{\"path\":\"/legal/terminos\"},{\"path\":\"/login\"},{\"path\":\"/penal\"},{\"path\":\"/planes\"},{\"path\":\"/planesbeta\"},{\"path\":\"/press\"},{\"path\":\"/reset-password\"},{\"path\":\"/transparencia\"},{\"path\":\"/auth/callback\"},{\"path\":\"/master/ab-experiments\"},{\"path\":\"/master/ai-abuse\"},{\"path\":\"/master/ai-costs\"},{\"path\":\"/master/ai-governance\"},{\"path\":\"/master/analytics\"},{\"path\":\"/master/analytics/demographics\"},{\"path\":\"/master/api-keys\"},{\"path\":\"/master/approvals\"},{\"path\":\"/master/audit\"},{\"path\":\"/master/automation\"},{\"path\":\"/master/backups\"},{\"path\":\"/master/billing\"},{\"path\":\"/master/bulk-actions\"},{\"path\":\"/master/compliance\"},{\"path\":\"/master/config\"},{\"path\":\"/master/cost-budgets\"},{\"path\":\"/master/cost-rates\"},{\"path\":\"/master/customer-risk\"},{\"path\":\"/master/dashboard\"},{\"path\":\"/master/dgii-sync\"},{\"path\":\"/master/dlq\"},{\"path\":\"/master/email-templates\"},{\"path\":\"/master/evidence-center\"},{\"path\":\"/master/feature-flags\"},{\"path\":\"/master/forecasting\"},{\"path\":\"/master/funnel\"},{\"path\":\"/master/funnel-metrics\"},{\"path\":\"/master/hardening\"},{\"path\":\"/master/health\"},{\"path\":\"/master/ia-consumption\"},{\"path\":\"/master/impersonation\"},{\"path\":\"/master/incident-response\"},{\"path\":\"/master/incidents\"},{\"path\":\"/master/infra\"},{\"path\":\"/master/legal-mode\"},{\"path\":\"/master/marketing\"},{\"path\":\"/master/metrics\"},{\"path\":\"/master/model-routing\"},{\"path\":\"/master/models\"},{\"path\":\"/master/notes\"},{\"path\":\"/master/notifications\"},{\"path\":\"/master/organizations\"},{\"path\":\"/master/passkeys\"},{\"path\":\"/master/plan-limits\"},{\"path\":\"/master/profit-intelligence\"},{\"path\":\"/master/promo-codes\"},{\"path\":\"/master/prompts\"},{\"path\":\"/master/quotas\"},{\"path\":\"/master/real-costs\"},{\"path\":\"/master/recommendations\"},{\"path\":\"/master/referrals\"},{\"path\":\"/master/revisiones\"},{\"path\":\"/master/roles\"},{\"path\":\"/master/security\"},{\"path\":\"/master/style-prefs\"},{\"path\":\"/master/support\"},{\"path\":\"/master/system-health\"},{\"path\":\"/master/system-state\"},{\"path\":\"/master/trials\"},{\"path\":\"/master/upgrade-intent\"},{\"path\":\"/master/users\"},{\"path\":\"/master/utm-builder\"},{\"path\":\"/master/vault\"},{\"path\":\"/master/vault-pending\"},{\"path\":\"/master/webhooks\"},{\"path\":\"/master-unlock\"}],\"isrRoutes\":[\"/blog/:slug\"]}";globalThis["_sentryNextJsVersion"] = "16.2.6";const DSN = (process.env.NEXT_PUBLIC_SENTRY_DSN || '').trim();
const ENV = process.env.NEXT_PUBLIC_SENTRY_ENV
  || process.env.NEXT_PUBLIC_VERCEL_ENV
  || (process.env.NODE_ENV === 'production' ? 'production' : 'development');

let sentryPromise = null;

// Red de seguridad: mientras Sentry todavía no cargó (ventana entre paint y
// el idle callback), guardamos errores crudos acá. Estos listeners son
// gratis (no traen el SDK) y se enganchan YA, synchronously — así ningún
// error de arranque se pierde aunque Sentry tarde en aparecer.
const earlyErrors = [];
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    earlyErrors.push({ type: 'error', value: e.error || e.message });
  });
  window.addEventListener('unhandledrejection', (e) => {
    earlyErrors.push({ type: 'rejection', value: e.reason });
  });
}

function loadSentry() {
  if (!DSN) return null;
  if (!sentryPromise) {
    sentryPromise = import('@sentry/nextjs').then((Sentry) => {
      Sentry.init({
        dsn: DSN,
        environment: ENV,
        // Release: usamos el git sha del commit deployado para correlacionar.
        release: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA?.slice(0, 12) || 'local',

        // Sample 10% de transacciones de performance en prod, 100% en dev.
        tracesSampleRate: ENV === 'production' ? 0.1 : 1.0,

        // Tracing distribuido browser↔server: cuando el browser hace fetch a
        // /api/*, Sentry propaga el trace ID en headers para que el span del
        // server se conecte al span del browser en el mismo trace. Útil para
        // debugar latencia end-to-end.
        integrations: [Sentry.browserTracingIntegration()],
        tracePropagationTargets: [
          'localhost',
          /^https:\/\/(www\.)?drvictorlegal\.com\/api/,
        ],

        // No grabamos sesiones por default (requiere replay package + RAM extra).
        // Si querés video de la sesión cuando el user encuentra un bug:
        //   replaysSessionSampleRate: 0,
        //   replaysOnErrorSampleRate: 1.0,
        //   integrations: [Sentry.replayIntegration()],
        // Lo dejamos comentado para no inflar el bundle.

        // Filtros de noise: errores que no nos interesan
        ignoreErrors: [
          // Cancelaciones de fetch del browser (user navegó antes de que termine)
          'AbortError',
          'NetworkError when attempting to fetch resource',
          // Extensiones del browser (no son nuestros)
          /chrome-extension:/,
          /moz-extension:/,
          // Bots/scrapers
          /googlebot/i,
          /bingbot/i,
        ],

        // Antes de enviar, scrubeamos data sensible
        beforeSend(event) {
          // No mandar passwords ni TOTP que pudieran haber quedado en form data
          if (event.request?.data && typeof event.request.data === 'object') {
            for (const k of ['password', 'newPassword', 'confirmPassword', 'totp', 'code']) {
              if (event.request.data[k]) event.request.data[k] = '[redacted]';
            }
          }
          // No mandar headers de auth/session
          if (event.request?.headers) {
            for (const k of ['authorization', 'cookie', 'x-session']) {
              if (event.request.headers[k]) event.request.headers[k] = '[redacted]';
            }
          }
          return event;
        },
      });
      // Flush: cualquier error que haya ocurrido ANTES de que Sentry
      // terminara de cargar (buffer de earlyErrors) se reporta ahora.
      earlyErrors.splice(0).forEach(({ value }) => Sentry.captureException(value));
      return Sentry;
    });
  }
  return sentryPromise;
}

if (DSN && typeof window !== 'undefined') {
  const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
  let triggered = false;
  const trigger = () => {
    if (triggered) return;
    triggered = true;
    INTERACTION_EVENTS.forEach((evt) => window.removeEventListener(evt, trigger));
    loadSentry();
  };
  INTERACTION_EVENTS.forEach((evt) => window.addEventListener(evt, trigger, { passive: true, once: true }));

  // Red de seguridad: si el visitante nunca interactúa (lee y se va), no
  // perdemos cobertura — igual carga, solo más tarde que antes (8s de idle
  // en vez de 4s, porque ahora preferimos que gane la interacción real).
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(trigger, { timeout: 8000 });
  } else {
    setTimeout(trigger, 5000);
  }
}

// Instrumenta las navegaciones del App Router (spans de transición de ruta).
// Si Sentry todavía no cargó (navegación muy temprana) o no hay DSN, es no-op.
export function onRouterTransitionStart(url, navigationType) {
  loadSentry()?.then((Sentry) => Sentry.captureRouterTransitionStart(url, navigationType));
}
